import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const hyeresEn: ChauffeurPage = {
  slug: "hyeres",
  title: "Private chauffeur & VTC in Hyères",
  subtitle:
    "Private chauffeur in Hyères: the city of palm trees, the Giens peninsula, the departure for the Golden Islands and Toulon-Hyères airport 15 min away.",
  keywords: [
    "private chauffeur hyères",
    "vtc hyères",
    "toulon-hyères airport transfer",
    "chauffeur giens peninsula",
  ],
  hero: {
    title: "Private chauffeur & VTC in Hyères",
    subtitle:
      "The city of palm trees, the Giens peninsula and the gateway to the Golden Islands: a chauffeur next to the airport.",
    keywords: [
      "VTC Hyères",
      "Giens peninsula chauffeur",
      "La Tour Fondue Porquerolles",
      "Toulon-Hyères airport",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur & VTC in Hyères",
    badges: [
      "Toulon-Hyères airport ~15 min",
      "Golden Islands departure",
      "FR/EN chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Hyères",
    items: [
      {
        title: "Airport 15 min away",
        description:
          "Toulon-Hyères airport sits within the town, around 15 min away; wait-free welcome and flight tracking.",
      },
      {
        title: "Gateway to the Golden Islands",
        description:
          "Link to La Tour Fondue for Porquerolles, and to the port for Port-Cros and Le Levant, luggage carried.",
      },
      {
        title: "Giens peninsula & salt marshes",
        description:
          "The double tombolo, the Pesquiers salt marshes and the peninsula beaches, reached with no parking worry.",
      },
      {
        title: "Rated 5.0/5",
        description:
          "Punctual, discreet chauffeurs based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Our chauffeur services in Hyères",
    services: [
      {
        title: "Toulon-Hyères airport transfer",
        text: "The nearest airport, around 15 min from the centre, with flight tracking and a personal welcome.",
      },
      {
        title: "Nice airport transfer",
        text: "For international flights, a link to Nice Côte d'Azur in close to 1h30, premium van on request.",
      },
      {
        title: "Islands departure & La Tour Fondue",
        text: "Drop-off at the Porquerolles pier at La Tour Fondue and at the port for Port-Cros and Le Levant.",
      },
      {
        title: "Toulon TGV station",
        text: "Transfer to Toulon station for TGV trains to Paris, alongside the Hyères regional station.",
      },
      {
        title: "Hourly hire & long distance",
        text: "A chauffeur by the day for the peninsula, or runs to Saint-Tropez, Cannes, Nice and Italy.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Old town of Hyères",
        description: "Medieval centre, Place Massillon and Parc Saint-Bernard.",
      },
      {
        name: "Villa Noailles",
        description: "Modernist villa and art centre above the town.",
      },
      {
        name: "Giens peninsula",
        description: "Double tombolo, beaches and the salt road.",
      },
      {
        name: "La Tour Fondue",
        description: "Ferry pier for Porquerolles, at the tip of Giens.",
      },
      {
        name: "Pesquiers salt marshes",
        description: "Former salt pans and flamingo lagoons.",
      },
      {
        name: "Toulon-Hyères airport",
        description: "Within the town, around 15 min from the centre.",
      },
    ],
  },
  generic: {
    heading: "Hyères, the city of palm trees facing the Golden Islands",
    subheading:
      "A spread-out town between old city, peninsula and airport, where a chauffeur simplifies every step.",
    paragraphs: [
      "Hyères, known as the city of palm trees, stretches from its medieval old town down to the sea and the Giens peninsula. The internal distances are real: between Place Massillon, the perched Villa Noailles and the double-tombolo beaches, a private chauffeur saves you time and drops you as close as possible, salt marshes and salt road included.",
      "The town is home to Toulon-Hyères airport, just fifteen minutes from the centre: it is the fastest entry point along this whole shore. For international flights, Nice Côte d'Azur is still close to 1h30, and Toulon's TGV station, alongside the Hyères station, opens the direct line to Paris.",
      "Hyères is also the gateway to the Golden Islands: we drop you at La Tour Fondue for the Porquerolles ferry, or at the port for Port-Cros and Le Levant. On the spot, hourly hire keeps the same chauffeur for your days, with fixed fares set in advance and an instant quote.",
    ],
    bullets: [
      "Airport 15 min away",
      "Drop-off at the island pier",
      "Fixed fares & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Hyères",
    steps: [
      "Tell us your flight (Toulon-Hyères or Nice), your island ferry or your address.",
      "We advise the vehicle to suit the luggage, the group and the distance.",
      "The chauffeur tracks the flight or the boat time and sets the departure.",
      "Direct transfer or hourly hire for the town and the peninsula.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Hyères"),
    {
      q: "Which airport is closest to Hyères?",
      a: "Toulon-Hyères airport is the nearest: it sits within the town, around 15 minutes from the centre. Nice Côte d'Azur, for long-haul flights, is close to 1h30.",
    },
    {
      q: "Do you drop off at La Tour Fondue for Porquerolles?",
      a: "Yes. We take you to the La Tour Fondue pier, on the Giens peninsula, for the Porquerolles ferry, and to the port of Hyères for Port-Cros and Le Levant.",
    },
    {
      q: "Do you handle transfers to Toulon TGV station?",
      a: "Yes, Toulon station serves TGV trains to Paris; we set the departure to your train time. The Hyères station handles the regional links.",
    },
    {
      q: "How far in advance should I book?",
      a: "Plan ahead in summer and during the Villa Noailles festivals; we also take last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to the international clientele of the islands and the peninsula.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "A Mercedes E-Class or S-Class sedan, a premium 7-seater van for groups and luggage, plus SUV and VIP minibus depending on the occasion.",
    },
    {
      q: "Do you cover long distances from Hyères?",
      a: "Yes: Saint-Tropez, Cannes, Nice, Monaco, Marseille and Italy, as well as runs from Cannes or Nice to Hyères, in a comfortable vehicle with a dedicated chauffeur.",
    },
  ],
  relatedLinks: [
    {
      label: "Car service on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Airport transfers in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Chauffeur service Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    {
      label: "Rent a car Hyères",
      href: "/en/location/car-rental-hyeres",
    },
  ],
  cta: {
    title: "Your private chauffeur in Hyères",
    text: "Airport 15 min away, departure for the Golden Islands or a day on the peninsula: book your chauffeur, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book / request a quote",
  },
  seo: {
    title: "Private chauffeur in Hyères — car service & airport transfer",
    description:
      "Private car service in Hyères: Toulon-Hyères airport 15 min away, Nice transfer, Toulon TGV station, Giens peninsula and departure for the Golden Islands.",
    canonical: "/en/chauffeur/hyeres",
  },
};

export default hyeresEn;
